<template>
  <div class="required-page">
    <div class="required-page__header">
      <h2 class="required-page__title">Потребности в материалах</h2>
      <q-chip color="grey-3" text-color="green-8" dense icon="local_shipping" class="q-ml-md">Поставщик</q-chip>
    </div>

    <CustomTable
      :rowsData="rows"
      :columns="columns"
      :loading="isLoading"
      :show-search="true"
      :custom-body="true"
      :update-data="loadData"
    >
      <template #customBody="props">
        <q-tr
          :props="props"
          class="cursor-pointer"
          @click="onRowClick(props.row)"
        >
          <custom-table-cell align="left">
            {{ props.row.required_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="left">
            {{ props.row.sbs_project_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="left">
            {{ props.row.executor_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="center">
            <q-icon
              :name="props.row.is_level ? 'check_circle' : 'cancel'"
              :color="props.row.is_level ? 'positive' : 'grey-5'"
              size="sm"
            />
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.date_begin || '-' }}
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.date_end || '-' }}
          </custom-table-cell>
          <custom-table-cell align="center">
            <div v-if="progressMap[props.row.required_id]" class="rq-mini-progress">
              <div class="rq-mini-bar">
                <div class="rq-mini-seg rq-mini--required" :style="{ width: miniPct(props.row.required_id, 'required') }"></div>
                <div class="rq-mini-seg rq-mini--ordered" :style="{ width: miniPct(props.row.required_id, 'ordered') }"></div>
                <div class="rq-mini-seg rq-mini--delivered" :style="{ width: miniPct(props.row.required_id, 'delivered') }"></div>
              </div>
              <q-tooltip>
                <div class="rq-tip-row"><span class="rq-tip-dot" style="background:#ff9800"></span> Потребность: {{ fmtNum(progressMap[props.row.required_id].required) }} ({{ miniPctNum(props.row.required_id, 'required') }}%)</div>
                <div class="rq-tip-row"><span class="rq-tip-dot" style="background:#2196f3"></span> Заказано: {{ fmtNum(progressMap[props.row.required_id].ordered) }} ({{ miniPctNum(props.row.required_id, 'ordered') }}%)</div>
                <div class="rq-tip-row"><span class="rq-tip-dot" style="background:#4caf50"></span> Доставлено: {{ fmtNum(progressMap[props.row.required_id].delivered) }} ({{ miniPctNum(props.row.required_id, 'delivered') }}%)</div>
                <div class="rq-tip-row"><span class="rq-tip-dot" style="background:#999"></span> Смета: {{ fmtNum(progressMap[props.row.required_id].smeta) }}</div>
              </q-tooltip>
            </div>
            <span v-else class="text-grey-5">—</span>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import instance from "@/services/api";
import CustomTable from "@/components/common/CustomTable/index.vue";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell.vue";

const columns = [
  { name: "required_name", label: "Наименование", align: "left", field: "required_name" },
  { name: "sbs_project_name", label: "Проект", align: "left", field: "sbs_project_name" },
  { name: "executor_name", label: "Исполнитель", align: "left", field: "executor_name" },
  { name: "is_level", label: "Учитывает этажи", align: "center", field: "is_level" },
  { name: "date_begin", label: "Дата начала", align: "center", field: "date_begin" },
  { name: "date_end", label: "Дата окончания", align: "center", field: "date_end" },
  { name: "progress", label: "Прогресс", align: "center", style: "width: 140px" },
];

export default {
  name: "RequiredSupplier",
  components: { CustomTable, CustomTableCell },
  data() {
    return {
      rows: [],
      columns,
      isLoading: false,
      progressMap: {},
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    fmtNum(v) {
      if (v == null || v === '') return '';
      const n = Number(v);
      if (isNaN(n)) return v;
      return parseFloat(n.toFixed(2));
    },
    miniPctNum(id, type) {
      const p = this.progressMap[id];
      if (!p || !p.smeta) return 0;
      const val = p[type] || 0;
      return parseFloat((val / p.smeta * 100).toFixed(1));
    },
    miniPct(id, type) {
      const pct = this.miniPctNum(id, type);
      if (pct <= 0) return '0';
      const minPx = { required: 6, ordered: 4, delivered: 2 };
      return `max(${pct}%, ${minPx[type] || 2}px)`;
    },
    async loadData() {
      this.isLoading = true;
      try {
        const { data } = await instance().get("/required/read/");
        this.rows = data?.data || [];
        this.loadAllProgress();
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async loadAllProgress() {
      const toNum = (v) => parseFloat(String(v).replace(',', '.')) || 0;
      const results = await Promise.allSettled(
        this.rows.map(r =>
          instance().get(`/required/${r.required_id}/progress/`).then(resp => {
            const pRows = resp.data?.data;
            if (pRows && pRows.length) {
              const p = pRows[0];
              return {
                id: r.required_id,
                smeta: toNum(p.smeta_amount),
                required: toNum(p.required_amount),
                ordered: toNum(p.ordered_amount),
                delivered: toNum(p.delivered_amount),
              };
            }
            return null;
          })
        )
      );
      const map = {};
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) {
          map[r.value.id] = r.value;
        }
      }
      this.progressMap = map;
    },
    onRowClick(row) {
      this.$router.push(`/required-supplier/${row.required_id}/items`);
    },
  },
};
</script>

<style scoped>
.required-page {
  padding: 16px;
}
.required-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.required-page__title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.rq-mini-progress {
  display: inline-block;
  width: 100%;
  min-width: 80px;
  max-width: 120px;
}
.rq-mini-bar {
  position: relative;
  height: 10px;
  background: #e8e8e8;
  border-radius: 5px;
  overflow: hidden;
}
.rq-mini-seg {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-radius: 0 5px 5px 0;
  transition: width 0.4s ease;
}
.rq-mini--required { background: #ff9800; z-index: 1; }
.rq-mini--ordered  { background: #2196f3; z-index: 2; }
.rq-mini--delivered { background: #4caf50; z-index: 3; }

.rq-tip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.6;
}
.rq-tip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
